import { Component } from "react";
import Format from "src/utils/Format";
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler";
import StateConstants from "src/states/StateContstants";
import ApiHandler from "src/api/ApiHandler";
import navigationService from "src/navigation/NavigationService";
import AddressThumbnail from "src/components/address_images/AddressThumbnail";
import PropertyFeatures from "src/components/general/PropertyFeatures";


interface ComparableHomeProps {
    comparableHome: any;
    onRemove: (id: string) => void;
    hideRemoveButton?: boolean;
    onCardSelect?: () => void;
}

export default class ComparableHome extends Component<any> {
    stateHandler: EventAdaptingStateHandler;
    apiHandler: ApiHandler;
    key: string;
    comparableHome: any;

    constructor(props: any) {
        super(props);
        this.stateHandler = props.stateHandler;
        this.apiHandler = props.apiHandler;
        this.key = props.key;
        this.comparableHome = props.comparableHome;
        this.state = {
            isMobile: this.stateHandler.get(StateConstants.IS_MOBILE)
        };
        this.stateHandler.addListener(StateConstants.IS_MOBILE, (state: any) => {
            this.setState({
                isMobile: state
            });
        });
    }

    componentDidMount() {
        this.stateHandler.addListener(StateConstants.MAP_MARKER_HOVER, (state: any) => {
            this.setState({
                hoverMarkerId: state
            });
        });
    }

    handleRemove = () => {
        this.props.onRemove(this.comparableHome["id"]);
    }

    handleNavigate = async () => {
        const propertyId = this.comparableHome["id"];
        if (propertyId && this.apiHandler) {
            await navigationService.navigateToValuation(propertyId, this.apiHandler);
        }
    }

    handleCardSelect = () => {
        this.stateHandler.set(StateConstants.MAP_MARKER_HOVER, this.comparableHome["id"]);
        this.props.onCardSelect?.();
    }

    render() {
        let identifier = this.comparableHome["id"];
        const valuation = this.stateHandler.get(StateConstants.VALUATION_STATE);
        const isGoogleBot = valuation?.["metadata"]?.["gb"] === true;
        const tier = this.props.tier || "full";
        const blurDetails = tier === "blurred-details" || tier === "blurred-all";
        const blurImage = tier === "blurred-all";
        const showImage = !blurImage;

        let className = "comparable-home-card";
        if (this.stateHandler.get(StateConstants.MAP_MARKER_HOVER) === identifier) {
            className += " highlighted";
        }
        if (blurImage) {
            className += " comparable-card-blurred-all";
        }

        const fullAddress = this.comparableHome["a"] ?? "";
        const displayAddress = this.comparableHome["da"] || fullAddress;
        return (
            <div
                className={className}
                id={"comparable-home-card-" + this.key}
                onClick={this.handleCardSelect}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && this.handleCardSelect()}
            >
                <div className="card-layout">
                    {showImage && !isGoogleBot && identifier && fullAddress && this.apiHandler && (
                        <AddressThumbnail
                            locationId={identifier}
                            locationString={fullAddress}
                            apiHandler={this.apiHandler}
                        />
                    )}
                    {blurImage && (
                        <div className="address-thumbnail comparable-thumbnail-placeholder">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                <circle cx="12" cy="16" r="1" />
                            </svg>
                        </div>
                    )}
                    <div className="card-body">
                        <div className="card-content">
                            <div className="address-row">
                                {!blurDetails && (
                                    <button
                                        className="view-button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            this.handleNavigate();
                                        }}
                                        title="View this property's valuation"
                                        aria-label="View this property's valuation"
                                    >
                                        →
                                    </button>
                                )}
                                <div className="address-title">
                                    <span className={`address-text${blurImage ? ' comparable-value-blurred' : ''}`}>{displayAddress}</span>
                                </div>
                                {!this.props.hideRemoveButton && (
                                    <button
                                        className="remove-button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            this.handleRemove();
                                        }}
                                        title="Remove comparable home"
                                        aria-label="Remove comparable home"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>

                            <PropertyFeatures features={this.comparableHome["af"]} />

                            <div className={`financial-fields${blurDetails ? ' comparable-value-blurred' : ''}`}>
                                <div className="financial-field">
                                    <span className="info-label">Value</span>
                                    <span className="info-value value-amount" id={"comparable-home-value-field-" + identifier}>
                                        {Format.getFormattedCurrency(this.comparableHome["value"])}
                                    </span>
                                </div>
                                <div className="financial-field">
                                    <span className="info-label">Sale Price</span>
                                    <span className="info-value sale-price">
                                        {Format.getFormattedCurrency(this.comparableHome["sale_price"])}
                                    </span>
                                </div>
                                <div className="financial-field">
                                    <span className="info-label">Sale Date</span>
                                    <span className="info-value sale-date">
                                        {this.comparableHome["sale_date"]}
                                    </span>
                                </div>
                            </div>

                            <div className={`stats-bar${blurDetails ? ' comparable-value-blurred' : ''}`}>
                                <span className="stat-text">Value Change</span>
                                {(() => {
                                    const currentValue = this.comparableHome["value"];
                                    const salePrice = this.comparableHome["sale_price"];
                                    const change = currentValue - salePrice;
                                    const isPositive = change >= 0;
                                    return (
                                        <span className={`change-amount ${isPositive ? 'positive' : 'negative'}`}>
                                            {isPositive ? '▲' : '▼'} {Format.getFormattedCurrency(change)}
                                        </span>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
